/**
 * SEO utility functions for Naija Nest Properties.
 *
 * Handles slug generation, meta-tag helpers, and structured-data builders.
 */

const SITE_NAME = 'Naija Nest Properties';
const SITE_URL = 'https://naijanestproperties.com';
// "Moderated", not "verified" - NaijaNest doesn't own, guarantee, or verify
// individual listings (see the Terms page disclaimer), it moderates them
// before they go live. Keep this wording consistent with AboutPage.js.
const DEFAULT_DESCRIPTION =
  'Rent or book short-let apartments, houses, and duplexes across Nigeria, plus properties for sale and land, all moderated on Naija Nest Properties.';
const DEFAULT_IMAGE = `${SITE_URL}/logo192.png`;

/* ─── Slug generation ─────────────────────────────────────────────── */

/**
 * Generate an SEO-friendly slug from property data.
 * Example: "3-bedroom-duplex-for-sale-in-lekki-lagos-1774556127373"
 */
export function generatePropertySlug(property) {
  if (!property) {
    return '';
  }

  const parts = [];

  // Bedrooms prefix (e.g. "3-bedroom")
  if (property.bedrooms) {
    parts.push(`${property.bedrooms}-bedroom`);
  }

  // Property type (e.g. "duplex", "apartment") - falls back to "apartment"
  // when a listing has no type set, same fallback used for the on-page
  // display elsewhere (PropertyDetailsPage.js), so the slug never reads as
  // just a bare listing-type + location with no property type at all.
  const pType = property.propertyType || property.property_type || 'Apartment';
  if (pType !== 'Other') {
    parts.push(pType.toLowerCase());
  }

  // Listing type intent (e.g. "for-sale", "for-rent", "short-let")
  const lt = property.listing_type || property.listingType || property.type || 'rent';
  if (lt === 'buy') {
    parts.push('for-sale');
  } else if (lt === 'rent') {
    parts.push('for-rent');
  } else if (lt === 'short-let') {
    parts.push('short-let');
  }

  // Location (e.g. "in-lekki-lagos")
  const area = property.area || '';
  const state = property.state || '';
  if (area || state) {
    parts.push('in');
    if (area) {
      parts.push(area.toLowerCase());
    }
    if (state && state.toLowerCase() !== area.toLowerCase()) {
      parts.push(state.toLowerCase());
    }
  }

  // Append numeric ID at the end for uniqueness
  const id = property.id;
  const slug = parts
    .join('-')
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  return `${slug}-${id}`;
}

/**
 * Extract the numeric property ID from a slug.
 * The ID is always the last segment after the final hyphen.
 */
export function extractIdFromSlug(slug) {
  if (!slug) {
    return null;
  }
  // If it's purely numeric, return as-is (backwards compat)
  if (/^\d+$/.test(slug)) {
    return slug;
  }
  // Extract trailing number after last hyphen
  const match = slug.match(/-(\d+)$/);
  return match ? match[1] : slug;
}

/* ─── Meta tag helpers ────────────────────────────────────────────── */

/**
 * Build page-level meta for react-helmet-async.
 */
export function pageMeta({ title, description, path, image, noIndex = false }) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const desc = description || DEFAULT_DESCRIPTION;
  const url = `${SITE_URL}${path || ''}`;
  const img = image || DEFAULT_IMAGE;

  return { fullTitle, desc, url, img, noIndex };
}

/**
 * Build meta for a property detail page.
 */
export function propertyMeta(property) {
  if (!property) {
    return pageMeta({});
  }

  const lt = property.listing_type || property.type || 'rent';
  const intent = lt === 'buy' ? 'for Sale' : lt === 'short-let' ? '– Short-let' : 'for Rent';
  const pType = property.propertyType || property.property_type || 'Property';
  const area = property.area || '';
  const state = property.state || '';
  const locationStr = [area, state].filter(Boolean).join(', ');
  const beds = property.bedrooms ? `${property.bedrooms} Bedroom ` : '';
  const price = Number(property.price || 0).toLocaleString('en-NG');

  const title = `${beds}${pType} ${intent} in ${locationStr}`;
  const description = `₦${price} – ${property.title || title}. ${
    property.description
      ? property.description.slice(0, 140)
      : 'View details, photos, and contact info on Naija Nest Properties.'
  }`;

  const images = property.images
    ? typeof property.images === 'string'
      ? JSON.parse(property.images)
      : property.images
    : [];
  const image = images[0] || DEFAULT_IMAGE;

  // Listings that aren't fully approved yet, or are no longer available
  // (sold/rented/let/booked), shouldn't stay in Google's index - the API
  // returns them at their direct URL regardless of status (no visibility
  // filter on GET /properties/:id), so this is the only gate.
  const isApproved =
    (property.approval_status || 'approved') === 'approved' &&
    (property.moderation_status || 'approved') === 'approved';
  const isAvailable = (property.availability_status || 'available') === 'available';

  return pageMeta({
    title,
    description,
    path: `/property/${generatePropertySlug(property)}`,
    image,
    noIndex: !isApproved || !isAvailable,
  });
}

/**
 * Build meta for a location landing page.
 *
 * `path` should be the real current route (e.g. `/lagos/apartments`) rather
 * than reconstructed here - a location page can be filtered by property
 * type only (no listingType), by listing type only, both, or neither, and
 * re-deriving the slug from just state/area/listingType (the old approach)
 * silently pointed the canonical tag at the wrong URL for every page
 * without an explicit rent/sale/short-let keyword (e.g. /lagos/apartments
 * canonicalized to /lagos/for-rent).
 */
export function locationMeta({ state, area, listingType, propertyType, path }) {
  // Only state an intent when the page is actually filtered by one - matches
  // the on-page <h1> in LocationLandingPage.js, which likewise omits it when
  // no listing-type keyword was in the URL, instead of always defaulting to
  // "for Rent" the way this used to.
  const intent =
    listingType === 'buy'
      ? 'for Sale'
      : listingType === 'short-let'
        ? 'Short-let'
        : listingType === 'rent'
          ? 'for Rent'
          : '';
  const locationStr = area ? `${area}, ${state}` : state;
  const subject = propertyType ? `${propertyType}s` : 'Properties';
  const title = [subject, intent, `in ${locationStr}`].filter(Boolean).join(' ');
  const description =
    propertyType || intent
      ? `Browse moderated ${subject.toLowerCase()}${
        intent ? ` ${intent.toLowerCase()}` : ''
      } in ${locationStr} on Naija Nest Properties.`
      : `Browse moderated property listings in ${locationStr}. Find apartments, houses, duplexes, and land on Naija Nest Properties.`;

  return pageMeta({ title, description, path });
}

/* ─── JSON-LD Structured Data ─────────────────────────────────────── */

/**
 * Sitewide Organization schema - rendered once, globally (see App.js), not
 * per-page. Deliberately omits any verification/trust claim beyond what's
 * true: NaijaNest is the marketplace operator, not a guarantor of listings.
 */
export function organizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    // Names both halves of the site (marketplace + Property Guide) so this
    // sitewide entity isn't read as "listings only" - the Property Guide's
    // own landing page (BlogPage.js) still carries its own, more specific
    // metadata on top of this.
    description:
      'A Nigerian property marketplace for rentals, short-lets, sales and land, paired with the NaijaNest Property Guide - property insights, advice and market knowledge from across Nigeria.',
    sameAs: [],
  };
}

/**
 * Sitewide WebSite schema with a SearchAction, so Google can offer a
 * Sitelinks Search Box. The target must be a *real* working search URL -
 * see the `?q=` handling added to BrowsePage.js, which reads this param and
 * applies it as the state/location filter.
 */
export function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/browse?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Build JSON-LD for a single property listing (RealEstateListing schema).
 */
export function propertyJsonLd(property) {
  if (!property) {
    return null;
  }

  const lt = property.listing_type || property.type || 'rent';
  const images = property.images
    ? typeof property.images === 'string'
      ? JSON.parse(property.images)
      : property.images
    : [];
  const area = property.area || '';
  const state = property.state || '';
  const price = Number(property.price || 0);
  const isAvailable = (property.availability_status || 'available') === 'available';

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: property.title,
    description: property.description || property.title,
    url: `${SITE_URL}/property/${generatePropertySlug(property)}`,
    image: images.length ? images : undefined,
    offers: {
      '@type': 'Offer',
      price: price,
      priceCurrency: 'NGN',
      availability: isAvailable ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
      category: lt === 'buy' ? 'For Sale' : lt === 'short-let' ? 'Short-let' : 'For Rent',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: area,
      addressRegion: state,
      addressCountry: 'NG',
    },
    numberOfRooms: property.bedrooms || undefined,
    numberOfBathroomsTotal: property.bathrooms || undefined,
  };
}

/* ─── Location page content helpers ──────────────────────────────── */

const locationContent = {
  Lagos:
    "Lagos is Nigeria's economic hub and largest property market. From the upscale Ikoyi and Victoria Island to the rapidly developing Lekki corridor, Lagos offers diverse real estate opportunities for every budget.",
  FCT: "Abuja, Nigeria's capital city, is known for its well-planned layout and premium real estate. Areas like Maitama, Asokoro, and Wuse offer some of the country's most sought-after properties.",
  Rivers:
    "Port Harcourt, the capital of Rivers State, is the centre of Nigeria's oil industry. GRA, Old GRA, and Peter Odili Road are popular residential areas with a growing property market.",
  Oyo: 'Ibadan, one of the largest cities in West Africa, offers affordable property options compared to Lagos. Bodija, Jericho, and the new Eleyele corridor are investment hotspots.',
  Enugu:
    'Enugu, the "Coal City", is a growing real estate market in southeast Nigeria. New Haven, Independence Layout, and GRA are the most popular areas for rent and purchase.',
  Kano:
    'Kano, the commercial heart of northern Nigeria, blends centuries of trading history with steady modern development. Nassarawa GRA, Bompai, and the Sabon Gari district are established residential and commercial hubs.',
  Kaduna:
    'Kaduna, a former colonial administrative capital, still has the wide, tree-lined streets and civil-service housing stock to show for it. Barnawa, Malali, and the Kaduna GRA are popular with professionals and government workers.',
  Ogun:
    "Ogun State's proximity to Lagos, especially Abeokuta and the Sagamu-Ibafo-Mowe corridor, has made it a fast-growing dormitory market for commuters priced out of Lagos. Industrial and warehousing demand around Agbara adds a second growth driver.",
  Delta:
    "Warri and Asaba anchor Delta State's two distinct property markets: one shaped by oil-industry housing demand, the other by Asaba's role as state capital. Effurun, the DBS Road axis, and Okpanam are among the most active residential areas.",
  Edo:
    'Benin City pairs deep cultural heritage with a steadily modernising property market. GRA, Airport Road, and the Sapele Road corridor are long-standing choices for residential and commercial listings.',
  Anambra:
    "Anambra's two commercial centres, Onitsha's markets and Awka's status as state capital, drive distinct pockets of demand. Trans-Nkisi, the Amawbia axis near Awka, and Nnewi's industrial belt stay consistently active.",
  Imo: 'Owerri, Imo State\'s capital, has grown into one of the South-East\'s more sought-after residential markets. New Owerri, the World Bank Housing Estate, and the Wetheral Road axis are popular with returning professionals and civil servants.',
  Abia:
    "Aba's reputation as a manufacturing and trading powerhouse fuels steady demand for commercial and residential space alike, alongside Umuahia's role as the quieter state capital. Ogbor Hill and Aba GRA are consistently among the most searched areas, alongside Umuahia's own GRA.",
  Kwara:
    "Ilorin, Kwara's capital, sits at the crossroads of Nigeria's north and south, giving it a comparatively affordable and stable property market. GRA, Tanke, and the Fate Road corridor are popular with civil servants and University of Ilorin staff and students.",
  Plateau:
    'Jos, known for its cooler climate and tin-mining history, offers a comparatively relaxed pace and a steadily recovering property market. Rayfield, Jos GRA, and the Bukuru axis are established residential choices.',
  'Cross River':
    "Calabar's reputation for cleanliness and tourism, the annual Calabar Carnival draws crowds nationwide, supports a property market geared toward residents and short-let visitors alike. State Housing, Marian Road, and the Federal Housing Estate area are popular choices.",
  'Akwa Ibom':
    "Uyo, Akwa Ibom's capital, has seen substantial infrastructure investment over the past two decades, reshaping its residential landscape. Shelter Afrique Estate, Osongama Estate, and the Nwaniba Road corridor are among the most active areas.",
  Ondo:
    "Akure, Ondo State's capital, offers an affordable alternative to the bigger South-West cities while still serving as a regional administrative and educational centre. Alagbaka GRA, Oba-Ile, and the Ondo Road corridor see steady demand.",
  Osun:
    "Osogbo and Ile-Ife anchor Osun State's property market: one built on state administration, the other on Obafemi Awolowo University and centuries of Yoruba cultural heritage. Ring Road and the Government Reservation Area in Osogbo, alongside Ile-Ife's OAU axis, are frequently searched.",
  Benue:
    "Makurdi, Benue State's capital and a major agricultural trading centre on the River Benue, has a property market shaped by government and university demand. High Level, North Bank, and the Wurukum area are among the most active.",
};

export function getLocationContent(state) {
  return (
    locationContent[state] ||
    `Discover moderated property listings in ${state}, Nigeria. Browse apartments, houses, and land available for rent, sale, and short-let.`
  );
}

export { SITE_NAME, SITE_URL, DEFAULT_DESCRIPTION, DEFAULT_IMAGE };
